import React, { useRef, useState } from 'react';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { trackMetaEvent } from '../utils/metaPixel';
interface FormState {
  nombre: string;
  telefono: string;
  correo: string;
  modalidad: string;
}
const initialState: FormState = {
  nombre: '',
  telefono: '',
  correo: '',
  modalidad: 'Presencial'
};
const fieldClass = 'w-full border-0 border-b border-ink/20 bg-transparent px-0 py-4 font-sans text-base text-ink placeholder:text-steel-400/70 transition-colors duration-200 ease-out focus:border-champagne focus:outline-none focus:ring-0';
const labelClass = 'block font-sans text-[11px] font-medium uppercase tracking-[0.18em] text-steel';
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xdeopzbz';
export function LeadForm() {
  const [form, setForm] = useState<FormState>(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const leadTracked = useRef(false);
  const update = (key: keyof FormState, value: string) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: new FormData(formEl),
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        if (!leadTracked.current) {
          leadTracked.current = true;
          trackMetaEvent('Lead');
        }
        formEl.reset();
        setForm(initialState);
        setSubmitted(true);
      } else {
        const details = await response.text();
        console.error('Formspree error response:', response.status, details);
        setError('No pudimos enviar tu información. Por favor, inténtalo nuevamente.');
      }
    } catch (err) {
      console.error('Formspree request failed:', err);
      setError('No pudimos enviar tu información. Por favor, inténtalo nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };
  return <section id="formulario" className="w-full scroll-mt-20 bg-ivory-dark py-16 sm:py-24 lg:py-32" aria-labelledby="form-title" data-id="element-104" data-mp="2hw7c7kswll4o">
      <div className="mx-auto max-w-3xl px-5 sm:px-10 lg:px-12" data-id="element-105" data-mp="2cxw0t5myn2kc">
        {submitted ? <div className="py-10 text-center sm:py-16" data-id="element-106" data-mp="285m5tb6zncb7">
            <span className="mx-auto flex h-14 w-14 items-center justify-center border border-champagne" data-id="element-107" data-mp="237axx6097bvm">
              <CheckIcon className="h-6 w-6 text-champagne" strokeWidth={1.5} data-id="element-108" data-mp="1y8huk6eol196" />
            </span>
            <h2 className="mt-10 font-sans text-3xl font-light uppercase leading-tight tracking-[0.02em] text-ink sm:text-[2.75rem]" data-id="element-109" data-mp="1u6d0j6kbm2m0">
              ¡Gracias!
            </h2>
            <p className="mx-auto mt-6 max-w-xl font-sans text-[17px] font-light leading-relaxed text-steel sm:text-lg" data-id="element-110" data-mp="1p9w29jjeq7v7">
              Recibimos tus datos. Nos pondremos en contacto contigo para
              coordinar la conversación y responder tus preguntas.
            </p>
          </div> : <>
            <Reveal data-id="element-111" data-mp="ip0erderdltx">
              <h2 id="form-title" className="font-sans text-[2rem] font-light uppercase leading-[1.1] text-ink sm:text-5xl xl:text-[3.4rem]" data-id="element-112" data-mp="ipijkyfhl8dg">
                Coordinemos una
                <br data-id="element-113" data-mp="3m6s9gzka4jvt" />
                conversación.
              </h2>
              <p className="mt-7 max-w-xl font-sans text-[17px] font-light leading-relaxed text-steel sm:text-lg" data-id="element-114" data-mp="3r4tuhq19mt9x">
                Déjanos tus datos y una persona de nuestro equipo te contactará
                para coordinar.
              </p>
            </Reveal>

            <form action={FORMSPREE_ENDPOINT} method="POST" onSubmit={handleSubmit} className="mt-14 space-y-12 border-t border-ink/15 pt-12 sm:mt-16" data-id="element-115" data-mp="2yek4oi0ktz88">
              <div className="space-y-9" data-id="element-116" data-mp="14y7gzzw52yei">
                <div data-id="element-117" data-mp="14z8ueb0mnwze">
                  <label htmlFor="nombre" className={labelClass} data-id="element-118" data-mp="14vl0vbv6nako">
                    Nombre y apellido *
                  </label>
                  <input id="nombre" name="nombre" type="text" required autoComplete="name" value={form.nombre} onChange={e => update('nombre', e.target.value)} className={fieldClass} data-id="element-119" data-mp="1dxu7vrr8sltb" />
                </div>
                <div data-id="element-120" data-mp="1xmczsh3mudbp">
                  <label htmlFor="telefono" className={labelClass} data-id="element-121" data-mp="1xm39stil8660">
                    Teléfono *
                  </label>
                  <input id="telefono" name="telefono" type="tel" required autoComplete="tel" value={form.telefono} onChange={e => update('telefono', e.target.value)} className={fieldClass} data-id="element-122" data-mp="7ap0u405eu0h" />
                </div>
                <div data-id="element-123" data-mp="3fj6n9o6xohu0">
                  <label htmlFor="correo" className={labelClass} data-id="element-124" data-mp="3fjpx0hmfjzwy">
                    Correo electrónico *
                  </label>
                  <input id="correo" name="email" type="email" required autoComplete="email" value={form.correo} onChange={e => update('correo', e.target.value)} className={fieldClass} data-id="element-125" data-mp="35m1z5itj2cak" />
                </div>
              </div>

              <div className="border-t border-ink/12 pt-8" data-id="element-126" data-mp="ut7j7pt0eswo">
                <input type="hidden" name="modalidad" value={form.modalidad} data-id="element-127" data-mp="uro1durpg9ss" />
                <p className="font-sans text-[11px] font-medium uppercase tracking-[0.18em] text-champagne" data-id="element-128" data-mp="zsbs0fc0uomh">
                  Encuentro presencial
                </p>
                <p className="mt-3 font-sans text-base font-light text-steel" data-id="element-129" data-mp="tcbww65ophne">
                  Miami, FL 33172
                </p>
              </div>

              <div data-id="element-130" data-mp="ofl72r42v7os">
                <button type="submit" disabled={submitting} className="group inline-flex w-full items-center justify-center gap-3 bg-ink px-8 py-5 font-sans text-xs font-medium uppercase tracking-[0.2em] text-ivory transition-colors duration-200 ease-out hover:bg-ink-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto" data-id="element-131" data-mp="jee0gvdo8ejp">
                  {submitting ? 'Enviando...' : 'Agendar una conversación'}
                  {!submitting && <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-132" data-mp="1v1fg6voeqlyf" />}
                </button>

                {error && <p role="alert" className="mt-5 max-w-lg font-sans text-sm font-light leading-relaxed text-ink" data-id="element-133" data-mp="2cbj7xi71h03n">
                    {error}
                  </p>}

                <p className="mt-5 font-sans text-sm font-light leading-relaxed text-steel" data-id="element-134" data-mp="2lb6cudyz8o1g">
                  Coordinaremos contigo el día, horario y ubicación.
                </p>
              </div>
            </form>
          </>}
      </div>
    </section>;
}